import { Input, makeStyles, tokens } from "@fluentui/react-components";
import { useState } from "react";
import { AppDialog, DialogField } from "@/components/app-dialog";
import { FuiButton } from "@/components/fui-control";
import { SectionTitle } from "@/components/ui-typography";
import { grantAdminAccess, useMockPlanAccess } from "@/features/access/mock-plan-access";
import { formatDate } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  page: {
    height: "100%",
    minHeight: 0,
    overflow: "auto",
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    boxSizing: "border-box",
  },
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    maxWidth: "480px",
  },
  list: {
    margin: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    gap: tokens.spacingHorizontalS,
    padding: "6px 10px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  first: { borderTop: "none" },
  label: { color: tokens.colorNeutralForeground3, margin: 0 },
  value: {
    margin: 0,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  num: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  actions: { display: "flex" },
});

function byteLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
}

/** §11.3 워크스페이스 관리자(비멤버). 콘텐츠는 열지 않는다. */
export function PlanInfoPanel({ planId }: { planId: string }) {
  const styles = useStyles();
  const name = useCatalogStore((s) => s.projects[planId]?.name ?? "플랜");
  const project = useProjectStore((s) => (s.activeId === planId ? s.project : null));
  const access = useMockPlanAccess();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const owner = access.members.find((member) => member.role === "소유자")?.name ?? "—";
  const members = access.members.filter((member) => member.status === "활성").length;
  const size = project ? byteLabel(new TextEncoder().encode(JSON.stringify(project)).length) : "—";
  const activity = project?.updatedAt ? formatDate(project.updatedAt, "dateTime") : "—";

  const rows: { label: string; value: string; numeric?: boolean }[] = [
    { label: "이름", value: name },
    { label: "소유자", value: owner },
    { label: "멤버", value: String(members), numeric: true },
    { label: "상태", value: "활성" },
    { label: "크기", value: size, numeric: true },
    { label: "공유 링크", value: String(access.shares.length), numeric: true },
    { label: "마지막 활동", value: activity },
    { label: "가시성", value: "멤버만" },
  ];

  const confirm = () => {
    grantAdminAccess(planId, reason);
    setReason("");
    setOpen(false);
  };

  return (
    <div className={styles.page}>
      <section className={styles.panel} aria-label="플랜 정보">
        <SectionTitle>플랜 정보</SectionTitle>
        <dl className={styles.list}>
          {rows.map((row, index) => (
            <div key={row.label} className={index === 0 ? `${styles.row} ${styles.first}` : styles.row}>
              <dt className={styles.label}>{row.label}</dt>
              <dd className={row.numeric ? `${styles.value} ${styles.num}` : styles.value} title={row.value}>{row.value}</dd>
            </div>
          ))}
        </dl>
        <div className={styles.actions}>
          <FuiButton appearance="primary" onClick={() => setOpen(true)}>관리자로 접근</FuiButton>
        </div>
      </section>
      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        title="관리자로 접근"
        description="30일 동안 열람자로 이 플랜을 봅니다."
        width="sm"
        footer={
          <>
            <FuiButton onClick={() => setOpen(false)}>취소</FuiButton>
            <FuiButton appearance="primary" onClick={confirm}>접근</FuiButton>
          </>
        }
      >
        <DialogField label="사유" hint="선택">
          <Input size="small" value={reason} onChange={(_, data) => setReason(data.value)} aria-label="사유" />
        </DialogField>
      </AppDialog>
    </div>
  );
}
